import classNames from 'classnames';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { InjectedIntlProps } from 'react-intl';
import { Link } from 'react-router-dom';
import { InjectedFormProps, SubmitHandler } from 'redux-form';
import styled from 'styled-components';

import Button from '../../../components/Button';
import * as Form from '../../../components/Form';
import IMPComponent from '../../../components/interfaces/IMPComponent';
import PATHS from '../../../constants/paths';
import AuthFormErrors from './AuthFormErrors';

interface IFormData {
  email?: string;
  password?: string;
}

export interface IAuthFormProps extends IMPComponent, InjectedFormProps<IFormData, {}, {}> {
  linkPath: string;
  messageBase: string;
  passwordLess?: boolean;
  globalErrors?: string[];
  onSubmit: SubmitHandler;
}

const AuthFormHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 6.25rem;
  padding-bottom: 1.25rem;
`;

const AuthFormTitle = styled.div`
  font-size: ${p => p.theme.font.size.XLarge};
  font-weight: ${p => p.theme.font.weight.bold};
`;

const AuthFormField = styled.div`
  & + & {
    margin-top: 1.25rem;
  }
`;

const ActionsBlock = styled.div`
  margin-top: 1.25rem;
`;

const LinksBlock = styled(ActionsBlock)`
  margin-top: 2rem;
  text-align: center;
`;

const FormLink = styled(Link)`
  color: ${p => p.theme.colorCerulean};
  text-decoration: none;
  font-weight: ${p => p.theme.font.weight.medium};
  margin-left: 0.5rem;
`;

const ForgotPasswordSection = styled.section`
  margin-top: 1.25rem;
  text-align: right;
  font-size: ${p => p.theme.font.size.small};
`;

const AuthForm: React.FunctionComponent<IAuthFormProps & InjectedIntlProps> = ({
  className,
  style,
  linkPath,
  messageBase,
  passwordLess,
  invalid,
  asyncValidating,
  globalErrors,
  intl: { formatMessage },
  onSubmit,
}) => {
  const cn = classNames('AuthForm', className);

  return (
    <form action="/" className={cn} style={style} onSubmit={onSubmit} noValidate={true}>
      <AuthFormHeader>
        <AuthFormTitle>
          {formatMessage({
            id: `${messageBase}.title`,
          })}
        </AuthFormTitle>
        {!isEmpty(globalErrors) && <AuthFormErrors errors={globalErrors} />}
      </AuthFormHeader>
      <AuthFormField>
        <Form.EmailField
          withErrorBelow={true}
          required={true}
          name="email"
          label={formatMessage({ id: `${messageBase}.email` })}
        />
      </AuthFormField>
      {!passwordLess && (
        <AuthFormField>
          <Form.PasswordField
            withErrorBelow={true}
            required={true}
            name="password"
            label={formatMessage({ id: `${messageBase}.password` })}
          />
        </AuthFormField>
      )}
      {linkPath === PATHS.AUTH.SIGN_UP && (
        <ForgotPasswordSection>
          <FormLink to={PATHS.AUTH.RESET_PASSWORD}>
            {formatMessage({ id: 'containers.Auth.AuthForm.forgotPassword' })}
          </FormLink>
        </ForgotPasswordSection>
      )}
      <ActionsBlock>
        <Button type="submit" block={true} disabled={invalid || !!asyncValidating}>
          {formatMessage({ id: `${messageBase}.submit` })}
        </Button>
      </ActionsBlock>
      <LinksBlock>
        <span>{formatMessage({ id: `${messageBase}.linkLabel` })}</span>
        <FormLink to={linkPath}>{formatMessage({ id: `${messageBase}.linkText` })}</FormLink>
      </LinksBlock>
    </form>
  );
};

AuthForm.defaultProps = {
  passwordLess: false,
  globalErrors: [],
};

export default React.memo(AuthForm);



// WEBPACK FOOTER //
// ./src/containers/Auth/AuthForm/index.tsx